<template>
  <div>
    <fetching-data-spinner
      :error="fetchError"
      :loading="fetch"
      :loading-title="$t('reply.form.loading-reply')"
      enable-retry
      @retry="fetchInstaReply"
    />
    <div ref="form">
      <b-card v-if="!fetch">
        <error-alert
          v-if="error"
          :error="error"
        />
        <b-row>
          <template v-if="replyTemplates.length && !edit && !isTemplate && !story">
            <b-col
              md="6"
              sm="12"
              class="mb-1"
            >
              <b-form-checkbox
                v-model="fromSaved"
                switch
              >
                {{ $t('reply.form.choose_saved_template') }}
              </b-form-checkbox>
              <div
                class="ml-2"
              >
                <general-templates-select
                  v-if="fromSaved"
                  v-model="templateId"
                  :label="$t('reply.form.choose-reply-template')"
                  :clearable="true"
                  :options="replyTemplates"
                />
              </div>
            </b-col>
            <b-col md="12" />
          </template>
          <b-col
            v-if="!story"
            md="6"
            sm="12"
          >
            <ok-text-input
              v-model="form.name"
              :label="$t('reply.template_name')"
              is-required
            />
          </b-col>
          <b-col md="12" />
          <b-col
            v-if="!story"
            md="12"
            class="mt-2"
          >
            <b-form-checkbox
              v-model="form.comment_enabled"
              switch
            >
              {{ $t('reply.form.enable_comment_reply') }}
            </b-form-checkbox>
          </b-col>
          <b-col
            v-if="form.comment_enabled && !story"
            md="12"
          >
            <p class="text-muted">
              {{ $t('instagram.comment-hint') }}
            </p>
            <b-row>
              <b-col
                v-for="(comment,index) in form.comment"
                :key="index"
                md="3"
                sm="12"
              >
                <ok-text-input
                  :key="'input'+index"
                  v-model="form.comment[index]"
                  is-required
                  :is-append-text="false"
                  is-append-merged
                  :label="$t('instagram.comment-count-label',{num:index+1})"
                >
                  <template
                    v-if="index>0"
                    #append
                  >
                    <b-button
                      size="sm"
                      variant="danger"
                      @click="removeComment(index)"
                    >
                      <FeatherIcon icon="TrashIcon" />
                    </b-button>
                  </template>
                </ok-text-input>
              </b-col>
              <b-col
                md="1"
                class="d-flex align-items-center mt-1"
              >
                <b-button
                  variant="primary"
                  size="sm"
                  @click="form.comment.push('')"
                >
                  <FeatherIcon icon="PlusIcon" />
                </b-button>
              </b-col>
            </b-row>
          </b-col>
          <b-col
            v-if="form.comment_enabled && !story"
            class="mt-1"
            md="12"
          >
            <div
              class="comment-number-container"
            >
              <label
                for="comment-num"
                class="flex-grow-1"
              >

                {{ $t('reply.form.comment-delay') }} :</label>
              <b-form-spinbutton
                id="comment-num"
                v-model.number="form.delay"
                class="ml-sm-1 ml-md-3 flex-grow-0"
                style="max-width: 293px"
                :min="0"
                :max="120"
              />
            </div>
          </b-col>
          <b-col
            v-if="form.comment_enabled && !story && !isTemplate && accountCanUseApi"
            md="12"
            class="mt-2"
          >
            <div
              class="p-2"
              style="border: 1px dashed #6f42c1; border-radius: 6px"
            >
              <b-form-checkbox
                v-model="form.ai_enabled"
                switch
              >
                {{ $t('reply.form.ai_comment_reply') }}
              </b-form-checkbox>
              <h6
                style="font-size: 12px"
                class="text-muted mt-1"
              >
                {{ $t('reply.form.ai_comment_reply_hint') }}
              </h6>
              <p
                v-if="Number(currentAccount.ai_reply_limit) > 0"
                class="text-muted small mb-1"
              >
                {{ $t('reply.form.ai_pool_status', { used: Number(currentAccount.ai_comment_replies_used) || 0, cap: Number(currentAccount.ai_reply_limit) }) }}
              </p>
              <div class="mt-2">
                <label
                  class="d-block small font-weight-bold"
                  for="ig-reply-ai-limit"
                >{{ $t('reply.form.ai_limit') }}</label>
                <b-form-spinbutton
                  id="ig-reply-ai-limit"
                  v-model.number="form.ai_limit"
                  class="mt-1"
                  style="max-width: 220px"
                  min="0"
                  :max="maxAiLimitForReply"
                />
                <h6
                  style="font-size: 12px"
                  class="text-muted mt-1"
                >
                  {{ $t('reply.form.ai_limit_hint') }}
                </h6>
              </div>
              <ok-text-input
                v-model="form.ai_post_description"
                class="mt-2"
                :label="$t('reply.form.ai_post_description')"
                textarea
                :rows="4"
              />
              <h6
                style="font-size: 12px"
                class="text-warning mt-1"
              >
                {{ $t('reply.form.ai_post_description_hint') }}
              </h6>
            </div>
          </b-col>
          <b-col
            v-if="!story"
            md="12"
            class="mt-2"
          >
            <hr>
            <div
              class="ml-2"
            />
            <b-form-checkbox
              v-model="form.message_enabled"
              class="mt-2"
              switch
            >
              {{ $t('reply.form.enable_message_reply') }}
            </b-form-checkbox>
          </b-col>
          <b-col
            v-if="story || form.message_enabled"
            md="12"
            class="mt-2"
          >
            <Types
              ref="replyTypes"
              :tab-i="typTabIndex"
              :is-facebook="false"
            />
          </b-col>
          <b-col
            v-if="!isTemplate && !story"
            md="12"
            class="mt-2"
          >
            <hr>
            <b-form-checkbox
              v-if="!edit && !fromSaved"
              v-model="save"
              class="mt-2"
              switch
            >
              {{ $t('reply.form.save_template') }}
            </b-form-checkbox>
            <b-form-checkbox
              v-model="schedule"
              class="mt-2"
              switch
            >
              {{ $t('reply.form.stop_at') }}
            </b-form-checkbox>
            <reply-schedule
              v-if="schedule"
              ref="schedule"
            />
          </b-col>
        </b-row>
        <hr>
        <div class="d-flex justify-content-center">
          <b-button
            :disabled="loading"
            variant="success"
            @click="submitReply"
          >
            <b-spinner
              v-if="loading"
              small
              class="ml-1"
            />
            {{ $t('forms.save') }}
          </b-button>
        </div>
      </b-card>
    </div>
  </div>

</template>

<script>
import { mapGetters } from 'vuex'
import {
  BCard, BRow, BCol, BFormCheckbox, BButton, BSpinner, BFormSpinbutton,
} from 'bootstrap-vue'
import OkTextInput from '@/@bya3/components/inputs/OkTextInput'
import ErrorAlert from '@/@bya3/components/alerts/ErrorAlert'
import FormsService from '@/services/forms-service'
import GeneralTemplatesSelect from '@/@bya3/components/inputs/GeneralTemplatesSelect'
import ReplySchedule from '@/views/owner-fb/reply/components/ReplySchedule'
import FetchingDataSpinner from '@/@bya3/components/utils/FetchingDataSpinner'
import InstagramReplyApi from '@/api/instagram/reply'
import Types from '@/views/owner-fb/reply/components/templates/ReplyTypes'

export default {
  name: 'ProcessReply',
  provide() {
    const form = {}
    Object.defineProperty(form, 'reply', {
      enumerable: true,
      get: () => this.form,
      set: val => { this.form = val },
    })
    return {
      form,
    }
  },
  components: {
    BFormSpinbutton,
    ReplySchedule,
    GeneralTemplatesSelect,
    FetchingDataSpinner,
    ErrorAlert,
    OkTextInput,
    Types,
    BCard,
    BRow,
    BCol,
    BFormCheckbox,
    BButton,
    BSpinner,
  },
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
    story: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      changeTypeReply: false,
      error: null,
      fromSaved: false,
      templateId: null,
      schedule: false,
      fetch: true,
      typTabIndex: 0,
      fetchError: null,
      save: false,
    }
  },
  computed: {
    ...mapGetters({
      form: 'reply/form',
      currentAccountId: 'instagram/currentAccountId',
      webviewEnabled: 'pages/webviewEnabled',
      accountCanUseApi: 'instagram/accountCanUseApi',
      replyTemplates: 'instagram/templates',
    }),
    currentAccount() {
      return this.$store.getters['instagram/currentAccount'] ?? {}
    },
    maxAiLimitForReply() {
      return Math.max(0, Number(this.currentAccount.ai_reply_limit ?? 0) - Number(this.currentAccount.ai_used ?? 0))
    },
  },
  watch: {
    currentAccountId() {
      this.fetchDependencies()
    },
    templateId(id) {
      this.useTemplate(id)
    },
  },
  async mounted() {
    this.changeTypeReply = false
    await this.fetchDependencies()
    if (this.edit) {
      await this.fetchInstaReply()
    } else {
      this.ensureAiLimitWithinRemaining()
      this.fetch = false
    }
  },
  methods: {
    async useTemplate(id) {
      const template = this.replyTemplates.find(t => t.id === id)
      if (template) {
        this.$store.commit('reply/SET_FORM', template)
        await this.setMessage(template)
      }
    },
    async fetchInstaReply() {
      const { id } = this.$route.params
      if (this.story) {
        await InstagramReplyApi.findStory(id).then(async res => {
          this.$store.commit('reply/SET_FORM', res.reply)
          this.setMessage(res.reply)
        }).catch(e => { this.fetchError = e; this.fetch = false })
      } else {
        await (this.isTemplate ? InstagramReplyApi.findTemplate(id) : InstagramReplyApi.find(id)).then(async res => {
          this.$store.commit('reply/SET_FORM', res.reply)
          this.setMessage(res.reply)
        }).catch(e => { this.fetchError = e; this.fetch = false })
      }
    },
    async fetchDependencies() {
      // Always refresh on add reply so newly saved templates appear
      const force = !this.edit && !this.isTemplate && !this.story
      await this.$store.dispatch('instagram/GET_DEPENDENCIES', force).catch(e => { this.error = e })
      if (force) {
        await this.$store.dispatch('instagram/GET_TEMPLATES', true).catch(() => {})
      }
      if (this.webviewEnabled) {
        await this.$store.dispatch('webview/GET_TEMPLATES').catch(e => { this.error = e })
      }
    },
    setMessage(reply) {
      // eslint-disable-next-line default-case
      switch (reply.message_type) {
        case 'text': {
          this.$store.commit('reply/SET_FORM', { message: reply.reply_message?.text ?? '' })
          break
        }
        case 'buttons':
        {
          this.$store.commit('reply/SET_FORM', {
            message: reply.reply_message?.attachment?.payload?.text ?? '',
            buttons: reply.reply_message?.attachment?.payload?.buttons ?? [],
          })
          this.typTabIndex = 1
          break
        }
        case 'generic':
        {
          this.$store.commit('reply/SET_FORM', { cards: reply.reply_message?.attachment?.payload?.elements ?? [] })
          this.typTabIndex = 2
          break
        }
        case 'image':
          this.$store.commit('reply/SET_FORM', {
            image: reply.reply_message?.attachment?.payload?.url ?? '',
          })
          this.typTabIndex = 3
          break
      }
      if (this.form?.stop_at) {
        this.schedule = true
      }
      this.ensureAiLimitWithinRemaining()
      this.fetch = false
    },
    ensureAiLimitWithinRemaining() {
      const max = this.maxAiLimitForReply
      const current = this.form?.ai_limit
      if (current === null || current === undefined || current === '') {
        this.$store.commit('reply/SET_FORM', { ai_limit: max })
        return
      }
      const normalized = Math.max(0, parseInt(String(current), 10) || 0)
      if (normalized > max) {
        this.$store.commit('reply/SET_FORM', { ai_limit: max })
      }
    },
    removeComment(index) {
      this.form.comment.splice(index, 1)
    },
    async submitReply() {
      this.error = null
      if (!this.story && !this.form.name) {
        this.error = 'اسم النموذج مطلوب'
        this.$notifyError('اسم النموذج مطلوب')
        return
      }
      if (!this.form.message_enabled && !this.form.comment_enabled) {
        this.error = 'يجب تفعيل الرسالة او التعليق'
        this.$notifyError('يجب تفعيل الرسالة او التعليق')
        return
      }
      try {
        let scheduleValid = true
        const isValid = true
        if (this.schedule) {
          if (this.$refs?.schedule?.$refs?.form) {
            scheduleValid = await this.$refs.schedule.$refs.form.validate()
          }
        }
        if (this.form.message_enabled) {
          await this.$refs?.replyTypes?.$refs[this.form.message_type][0].submit()
        }
        if (!isValid || !scheduleValid) {
          throw new Error(this.$tc('errors.field-invalid', 1))
        }

        await this.submitInstagram()
      } catch (e) {
        this.$notifyError(e)
        this.error = e
      }
    },
    async submitInstagram() {
      const accountId = this.currentAccountId
      const form = {
        name: this.form.name ?? 'Form',
        id: this.form?.id,
        message_enabled: this.form.message_enabled,
        comment_enabled: this.form.comment_enabled,
        message_type: this.form.message_type,
        media_caption: this.form.post_description,
        media_thumb: this.form.post_thumb,
        delay: this.form.delay ?? 5,
        account_id: accountId,
        save: this.save,
        media_id: this.$route.params?.id,
      }
      form.reply_message = this.getReplyMessage()
      if (this.schedule && !this.isTemplate) {
        form.stop_at = this.form.stop_at
        form.schedule = this.form.schedule
      }
      if (this.isTemplate && this.edit) form.id = this.$route.params?.id
      if (this.form?.comment?.length) form.comment = this.form.comment
      if (!this.isTemplate && !this.story) {
        form.ai_enabled = !!(this.form.comment_enabled && this.form.ai_enabled)
        form.ai_post_description = this.form.comment_enabled
          ? String(this.form.ai_post_description ?? '')
          : ''
        form.ai_limit = Math.max(0, parseInt(String(this.form.ai_limit ?? 100), 10) || 0)
      }
      const finalForm = FormsService.convertObjectToFormData(form)
      if (this.form.image) finalForm.append('file', this.form.image)
      this.loading = true
      if (this.story) {
        await InstagramReplyApi.addStoryReply(accountId, finalForm).then(res => {
          if (res.error === 0) {
            this.$notifySuccess(res.message ?? 'Added successfully')
            this.$store.commit('reply/SET_DEFAULT_FORM')
            this.$router.push({ name: 'owner:instagram-stories' })
          }
        }).catch(e => { this.error = e })
          .finally(() => { this.loading = false })
      } else {
        await (this.isTemplate ? InstagramReplyApi.addTemplate(finalForm) : InstagramReplyApi.addReply(accountId, finalForm)).then(res => {
          if (res.error === 0) {
            this.$notifySuccess(res.message ?? 'Added successfully')
            this.$store.commit('reply/SET_DEFAULT_FORM')
            if (this.isTemplate || this.save) {
              this.$store.commit('instagram/SET_DEPENDENCIES_FETCHED', false)
              this.$store.commit('instagram/SET_TEMPLATES_FETCHED', false)
            }
            this.$router.push({ name: this.isTemplate ? 'owner:instagram-reply-templates' : 'owner:instagram-reports' })
          }
        }).catch(e => { this.error = e })
          .finally(() => { this.loading = false })
      }
    },
    getReplyMessage() {
      switch (this.form.message_type) {
        case 'text': return { text: this.form.message }
        case 'buttons': return {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'button',
              text: this.form.message,
              buttons: this.form.buttons,
            },
          },
        }
        case 'generic': return {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'generic',
              image_aspect_ratio: 'horizontal',
              elements: this.form.cards,
            },
          },
        }
        case 'image': return {
          attachment: {
            type: 'image',
            payload: {
              is_reusable: true,
              url: '',
            },
          },
        }
        default: return false
      }
    },
  },
}
</script>
